class QuickAddBuilder extends HTMLElement {
  constructor() {
    super();

    this._selectedItems = [];
    this._filters = [];
    this.minItems = parseInt(this.getAttribute('min-items')) || 2;
    this.maxItems = this.getAttribute('max-items') ? parseInt(this.getAttribute('max-items')) : null;

    this.groups = null;
    this.cards = null;
    this.footer = null;
    this.filterBtns = null;
    this.clearFiltersLink = null;

    this.addItem = this.addItem.bind(this);
    this.removeItem = this.removeItem.bind(this);
    this.onFilterClick = this.onFilterClick.bind(this);
    this.onClearFiltersClick = this.onClearFiltersClick.bind(this);
  }

  set selectedItems(items) {
    this._selectedItems = items;
    this._onSelectionChange();
  }
  get selectedItems() {
    return this._selectedItems;
  }

  connectedCallback() {
    this.groups = Array.from(this.querySelectorAll('.quick-add-builder-group'));
    this.cards = Array.from(this.querySelectorAll('quick-add-builder-product-card'));
    this.footer = this.querySelector('quick-add-builder-footer');

    this.addEventListener('quick-add-builder-add-item', (e) => this.addItem(e.detail));
    this.addEventListener('quick-add-builder-remove-item', (e) => this.removeItem(e.detail));

    this.filterBtns = Array.from(this.querySelectorAll('.quick-add-builder-filters__tag'));
    this.filterBtns.forEach((btn) => btn.addEventListener('click', this.onFilterClick));

    this.clearFiltersLink = this.querySelector('.quick-add-builder-filters__clear');
    this.clearFiltersLink?.addEventListener('click', this.onClearFiltersClick);

    this._applyFilters();
    this._onSelectionChange();
    this._prefillFromCart();
  }

  disconnectedCallback() {
    this.filterBtns?.forEach((btn) => btn.removeEventListener('click', this.onFilterClick));
    this.clearFiltersLink?.removeEventListener('click', this.onClearFiltersClick);
  }

  addItem({ productId, variantId, productTitle, productColor, productImage, price, compareAtPrice }) {
    const totalQty = this._selectedItems.reduce((sum, i) => sum + i.quantity, 0);
    if (this.maxItems != null && totalQty >= this.maxItems) return;

    const items = [...this._selectedItems];
    const idx = items.findIndex((i) => i.variantId === variantId);
    if (idx > -1) items[idx].quantity += 1;
    else items.push({ productId, variantId, productTitle, productColor, productImage, price, compareAtPrice, quantity: 1 });

    this.selectedItems = items;
  }

  removeItem({ variantId }) {
    const items = [...this._selectedItems];
    const idx = items.findIndex((i) => i.variantId === variantId);
    if (idx === -1) return;
    items[idx].quantity -= 1;
    if (items[idx].quantity === 0) items.splice(idx, 1);

    this.selectedItems = items;
  }

  onFilterClick(event) {
    const btn = event.currentTarget;
    const filter = btn.dataset.filter;
    const pressed = btn.getAttribute('aria-pressed') === 'true';

    if (pressed) {
      this._filters = this._filters.filter((f) => f !== filter);
      btn.setAttribute('aria-pressed', 'false');
    } else {
      this._filters.push(filter);
      btn.setAttribute('aria-pressed', 'true');
    }

    this._applyFilters();
  }

  onClearFiltersClick(event) {
    event.preventDefault();
    this._filters = [];
    this.filterBtns?.forEach((btn) => btn.setAttribute('aria-pressed', 'false'));
    this._applyFilters();
  }

  _applyFilters() {
    const hasFilters = this._filters.length > 0;

    this.cards.forEach((card) => {
      const tags = (card.dataset.tags || '').split(',').map((t) => t.trim());
      const matches = !hasFilters || this._filters.some((f) => tags.includes(f));
      card.hidden = !matches;
    });

    this.groups?.forEach((group) => {
      const groupCards = Array.from(group.querySelectorAll('quick-add-builder-product-card'));
      const anyVisible = groupCards.some((card) => !card.hidden);
      group.hidden = !anyVisible;
    });

    if (this.clearFiltersLink) this.clearFiltersLink.hidden = !hasFilters;

    if (this.emptyState) this.emptyState.hidden = true;
    const anyGroupVisible = !this.groups || this.groups.length === 0 || this.groups.some((g) => !g.hidden);
    this.emptyState = this.emptyState || this.querySelector('.quick-add-builder-empty-state');
    if (this.emptyState) this.emptyState.hidden = anyGroupVisible;
  }

  /**
   * If this offer is already in the cart (e.g. the shopper followed the cart drawer's Edit
   * link back here), pre-select the matching products and flip the footer into edit mode so
   * the next Add To Cart click replaces the existing line items instead of stacking on them.
   * Mirrors subscription-builder.js's handleEditSubscriptionByKey.
   */
  _prefillFromCart() {
    const offerId = this.footer?.offerId;
    if (!offerId || !this.cards?.length) return;

    const cart = window.__abc?.ShopifyCartData?.cart;
    const matchingCartItems = (cart?.items || []).filter(
      (item) => item.properties != null && item.properties._offer === offerId
    );
    if (!matchingCartItems.length) return;

    const items = [];
    matchingCartItems.forEach((cartItem) => {
      const card = this.cards.find((c) => String(c.variantId) === String(cartItem.variant_id));
      if (!card) return; // product no longer offered here — its old line still gets removed on save
      items.push({
        productId: card.productId,
        variantId: card.variantId,
        productTitle: card.productTitle,
        productColor: card.productColor,
        productImage: card.productImage,
        price: card.price,
        compareAtPrice: card.compareAtPrice,
        quantity: cartItem.quantity,
      });
    });

    this.footer?.setEditMode?.(matchingCartItems);
    if (items.length) this.selectedItems = items;
  }

  _onSelectionChange() {
    const totalQty = this._selectedItems.reduce((sum, i) => sum + i.quantity, 0);
    this.cards.forEach((card) => {
      const match = this._selectedItems.find((i) => i.variantId === card.variantId);
      card.updateProperties({ quantity: match?.quantity || 0, totalSelected: totalQty });
    });

    this.footer?.updateProperties({ selectedItems: this._selectedItems });
  }
}

if (!customElements.get('quick-add-builder')) customElements.define('quick-add-builder', QuickAddBuilder);
